/**
 * Logs Tab Component
 *
 * Displays system logs from Lambda functions using the LogViewer component.
 */

import { Skeleton } from '@/components/ui/skeleton';
import { LogViewer } from '@/components/Dashboard';
import { DashboardSection } from './DashboardSection';
import { TimeRangeSelector } from './TimeRangeSelector';
import type { LogEvent, TimeRange } from '@/types/dashboard';

interface LogsTabProps {
  events: LogEvent[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  lastUpdated: Date | null;
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
  selectedLogGroup: string;
  onLogGroupChange: (group: string) => void;
}

export function LogsTab({
  events,
  isLoading,
  isLoadingMore,
  error,
  hasMore,
  loadMore,
  lastUpdated,
  timeRange,
  onTimeRangeChange,
  selectedLogGroup,
  onLogGroupChange,
}: LogsTabProps) {
  return (
    <div className="space-y-6">
      {/* Header with Time Range Selector */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-muted-foreground h-5 flex items-center">
          {lastUpdated ? (
            <>
              Last updated: {lastUpdated.toLocaleTimeString()}
              <span className="ml-2 text-xs">(live updates)</span>
            </>
          ) : (
            <Skeleton className="h-4 w-56" />
          )}
        </div>
        <TimeRangeSelector value={timeRange} onChange={onTimeRangeChange} />
      </div>

      <DashboardSection
        id="logs"
        title="System Logs"
        description="View recent Lambda function logs for debugging and monitoring"
      >
        <LogViewer
          events={events}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          error={error}
          hasMore={hasMore}
          loadMore={loadMore}
          timeRange={timeRange}
          selectedLogGroup={selectedLogGroup}
          onLogGroupChange={onLogGroupChange}
        />
      </DashboardSection>
    </div>
  );
}
